import React from 'react';

import Tabs, { RenderStrategyOnFirstVisit } from 'CommonComponents/Tabs';

export const VIEW_HOME = 'home';
export const VIEW_REQUESTS = 'requests';
export const VIEW_APPROVALS = 'approvals';
export const VIEW_NOTIFICATIONS = 'notifications';
export const VIEW_DETAILS = 'details';

/**
 * Home's top-level tab strip: Overview, My requests, My approvals, My notifications,
 * My details.
 *
 * TAB VISIBILITY IS ENTITLEMENT-DRIVEN, NEVER QUEUE STATE. Each work tab is
 * gated on a `surfaces` flag from the Home payload, so an approver who has
 * cleared their queue keeps the tab and is told "all clear", and a user whose
 * scope was revoked keeps My Requests for the requests they already filed
 * (`surfaces.requests_list`, the module alone — not `surfaces.requests`, which
 * also requires somewhere to raise a new one).
 *
 * My details carries no `hidden` gate at all. It is the user's own profile and
 * password form, not event work, so there is no entitlement that could withhold
 * it without locking someone out of their own account.
 *
 * BUILT ON CommonComponents/Tabs, NOT FLOWBITE, and that is the point. Flowbite
 * builds its tab list from the triggers while the panels are gated separately,
 * and handed an id it has no item for it hides every panel and then throws — a
 * tab strip over a blank page. `Tabs` renders triggers and panels from ONE
 * array and filters `hidden` on both, so the divergence cannot happen. It also
 * brings the ARIA tablist/tab/tabpanel wiring and arrow-key navigation with it.
 *
 * Content is rendered on FIRST VISIT and then kept: a tab that refetches every
 * time it is reselected spins on data the browser still has, and one that
 * mounts them all up front fires list requests nobody asked for.
 */
export default function HomeTabs({ surfaces = {}, counts = {}, deltas = {}, notifications, activeView, onViewChange, panels }) {
    const badge = (value) => {
        if (typeof value !== 'number' || value <= 0) {
            return null;
        }

        return <span className="tw-ms-1.5 tw-rounded-[10px] tw-bg-white/25 tw-px-[7px] tw-py-px tw-text-[11px] tw-font-semibold">{value}</span>;
    };

    const title = (label, value) => (
        <>
            {label}
            {badge(value)}
        </>
    );

    const tabs = [
        {
            id: VIEW_HOME,
            /* THE LABEL ONLY. VIEW_HOME is still the string 'home' — it is what
               `?view=` carries, what a shared or bookmarked link contains, and
               what the entitlement check keys off. Renaming the value to match
               the label would break every link already out there. */
            title: $t('Overview'),
            content: panels[VIEW_HOME],
            renderStrategy: RenderStrategyOnFirstVisit,
        },
        {
            id: VIEW_REQUESTS,
            /* The badge counts returned requests, not open ones: the list holds
               approved and cancelled requests too, and the number worth putting
               on a tab is the one waiting on this user. */
            title: title($t('My requests'), deltas.requests_returned),
            hidden: !surfaces.requests_list,
            content: panels[VIEW_REQUESTS],
            renderStrategy: RenderStrategyOnFirstVisit,
        },
        {
            id: VIEW_APPROVALS,
            title: title($t('My approvals'), counts.approvals),
            hidden: !surfaces.approvals,
            content: panels[VIEW_APPROVALS],
            renderStrategy: RenderStrategyOnFirstVisit,
        },
        {
            id: VIEW_NOTIFICATIONS,
            title: title($t('My notifications'), notifications?.unread),
            hidden: !surfaces.notifications,
            content: panels[VIEW_NOTIFICATIONS],
            renderStrategy: RenderStrategyOnFirstVisit,
        },
        {
            id: VIEW_DETAILS,
            title: $t('My details'),
            content: panels[VIEW_DETAILS],
            renderStrategy: RenderStrategyOnFirstVisit,
        },
    ];

    return <Tabs tabs={tabs} activeTab={activeView} onTabActivate={onViewChange} />;
}
